// Le « ? » de l'en-tête de chaque écran principal (D-091, maquette c1) → la feuille « Sur cet écran » :
// à quoi sert l'écran, les gestes avec « Montre-moi », le support en dernier recours.
// À côté du « ? », pour les clubs pilotes : « 💬 Ton avis » (D-103, src/ui/feedback.ts).
import { gesturesOf, leadOf, SCREENS, screenKey, type HelpCtx, type ScreenGesture, type ScreenRole } from '../help/screens';
import { shownWith } from '../help/articles';
import { isMinorAccount, me, modOn, mvOpen, personaOf, siteOn, workTeams, type Ctx } from '../ui/context';
import { vouvoie } from '../ui/vous';
import { tourModule } from './tours';
import { isRtView } from '../ui/rt-shared';
import { html, openSheet } from '../ui/dom';
import { placeAvis } from '../ui/feedback';
import { startTour } from './engine';
import { TOURS } from './tours';

let watching: MutationObserver | null = null;

/** Pose le « ? » dans l'en-tête de l'écran affiché, s'il a son aide : l'en-tête de l'écran, sinon la ligne du
 *  retour (fiche de match, plan de séance, stats en direct), sinon l'en-tête d'une séance (D-101). Un écran qui
 *  se redessine lui-même (le plan de séance après chaque exercice, une page chargée en deux temps) le retrouve. */
export function addScreenHelp(view: HTMLElement, c: Ctx, hash: string): void {
  watching?.disconnect();
  watching = null;
  if (!SCREENS[screenKey(hash)]) return;
  placeHelp(view, c, hash);
  watching = new MutationObserver(() => {
    if (location.hash !== hash) { watching?.disconnect(); watching = null; return; }
    placeHelp(view, c, hash);
  });
  watching.observe(view, { childList: true });
}

function placeHelp(view: HTMLElement, c: Ctx, hash: string): void {
  const key = screenKey(hash);
  const head = view.querySelector<HTMLElement>('.screen-head') ?? view.querySelector<HTMLElement>('.pp-nav') ?? view.querySelector<HTMLElement>('.ev-head')
    ?? view.querySelector<HTMLElement>('.vg-band');   // la page d'un groupe de bénévoles : son bandeau (D-105)
  if (!head) return;
  if (head.querySelector('.gd-q')) { placeAvis(head, c, hash); return; }
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'gd-q';
  b.textContent = '?';
  b.setAttribute('aria-label', 'Aide sur cet écran');
  b.dataset.guide = 'screen-help';
  b.addEventListener('click', () => openScreenHelp(c, key, hash));
  head.classList.add('gd-has-q');
  head.append(b);
  placeAvis(head, c, hash);
}

/** Ce que l'aide sait du compte (src/help/screens.ts, HelpCtx) : de quoi dire ce que la page lui montre vraiment. */
export function helpCtx(c: Ctx): HelpCtx {
  const f = c.full, self = f.people.find(p => p.user_id === c.uid), shown = me(c);
  const minor = isMinorAccount(c), vous = vouvoie(c);   // D-103 : ni offre de partenaire, ni groupe ; un partenaire est vouvoyé
  const role: ScreenRole = isRtView(c) ? 'technique' : personaOf(c);
  return {
    role, minor, vous, on: k => modOn(c, k), hats: c.hats.map(h => h.key), benevoleHat: c.hat === 'benevole', otherHat: c.hats.some(h => h.key !== 'benevole'),
    parent: f.people.some(p => p.user_id !== c.uid),
    player: !!self && f.memberships.some(m => m.person_id === self.id),
    onTeam: f.people.some(p => f.memberships.some(m => m.person_id === p.id)),
    coach: f.pureCoach.size > 0, team: c.teams.length > 0,
    friend: c.coachOf.size > 0 || (!!shown && c.memberships.some(m => m.person_id === shown.id)),
    adult: !minor && !vous && role !== 'abonne',
  };
}

/** L'équipe que nomme l'adresse (#/matchs/<équipe>), sinon rien : l'écran montre tout le club (D-132). */
const teamOfRoute = (c: Ctx, hash: string) => hash.replace(/^#\/?/, '').split('/').map(s => c.full.teams.find(t => t.id === s)?.name).find(Boolean);

export function openScreenHelp(c: Ctx, key: string, hash = location.hash): void {
  const help = SCREENS[key];
  if (!help) return;
  const x = helpCtx(c);
  const vous = x.vous;
  const team = help.team ? (help.teamInRoute ? teamOfRoute(c, hash) : workTeams(c)[0]?.name) : undefined;
  const lead = leadOf(help, x);
  // D-122 : aucun geste d'un module éteint (celui de sa visite ou de son tuto).
  const offModule = (g: ScreenGesture) => [g.tour, g.tuto].some(id => !!id && !shownWith(id, tourModule(id), k => modOn(c, k), siteOn(c), mvOpen(c)));
  const gs = gesturesOf(help, x).filter(g => ((g.tour && TOURS[g.tour]) || g.tuto) && !offModule(g));
  openSheet('Sur cet écran', html`
    <div class="gd-sheet">
      <p class="gd-sheet-k">${help.label}${screenTeam(c, key, team) ? ` · ${screenTeam(c, key, team)}` : ''}</p>
      <p class="lead">${lead}</p>
      ${gs.length ? html`<div class="gd-list">${gs.map((g, i) => html`
        <div class="gd-row"><span class="t"><strong>${g.title}</strong><small>${g.sub}</small></span>
          ${g.tour && TOURS[g.tour] ? html`<button type="button" class="btn ${i === 0 ? 'btn-primary' : 'btn-ghost'} btn-sm gd-show" data-tour="${g.tour}">Montre-moi</button>`
            : html`<a class="btn btn-ghost btn-sm" href="#/aide/${g.tuto}">Le tuto</a>`}</div>`)}</div>` : ''}
      <a class="gd-note" href="#/aide/ecrire"><span aria-hidden="true">💬</span><span>Pas trouvé ? <b>Écrire au support</b> : ${vous ? 'la réponse arrive' : 'réponse'} dans la journée, dans l’app.</span></a>
    </div>`, (sheet, close) => {
    for (const b of sheet.querySelectorAll<HTMLButtonElement>('[data-tour]')) b.addEventListener('click', () => { close(); startTour(b.dataset.tour!); });
    for (const a of sheet.querySelectorAll<HTMLAnchorElement>('a[href]')) a.addEventListener('click', () => close());
  });
}

/** L'équipe que montre l'écran (B15) : celle de la route (#/matchs/<équipe>, #/t/<équipe>…) ; sans équipe dans la route,
 *  aucune pour le bureau ni pour Matchs (tout le club), sinon la première de mes équipes. */
function screenTeam(c: Ctx, key: string, fallback: string | undefined): string | undefined {
  if (fallback === undefined) return undefined;
  const parts = location.hash.replace(/^#\/?/, '').split(/[/?]/);
  const t = c.full.teams.find(x => parts.includes(x.id));
  if (t) return t.name;
  return key === 'matchs' || personaOf(c) === 'bureau' ? undefined : fallback;
}
